// Download-3D-model modal. Opened from the tool-rail "Download model" action.
//
// Flow:
//   1. On mount, call Unity "GetSceneDModel" to receive the current scene as
//      a DModel JSON blob. Show a spinner while we wait.
//   2. Once the blob lands, show one button per supported format. Each posts
//      the DModel JSON to /api/dmodel/export/{format} and downloads the
//      response. Disabled until the blob is ready.
//   3. Re-fetching the model is just closing and re-opening the modal — the
//      operator usually only downloads once.

import { useEffect, useRef, useState } from "react";
import type { ConfiguratorViewerHandle } from "../../../components/ConfiguratorViewer";
import { logDModelSummary } from "./debugDModel";

interface ExportFormat {
  id: "obj" | "glb" | "stl";
  label: string;
  description: string;
  extension: string;
}

const FORMATS: ExportFormat[] = [
  { id: "obj", label: "OBJ", description: "Wavefront OBJ — opens in most 3D tools.",  extension: "obj" },
  { id: "glb", label: "GLB", description: "Binary glTF — standard for web and AR.",   extension: "glb" },
  { id: "stl", label: "STL", description: "STereoLithography — used for 3D printing.", extension: "stl" },
];

/** Operator-authorable presented copy for the download modal. Optional fields
 *  fall back to the English defaults; the host resolves them from the
 *  modal:download-model slot's text blocks. Per-format descriptions are keyed by
 *  format id. Transient status/error strings are not authorable (system feedback). */
export interface DownloadModelModalContent {
  heading?: string;
  intro?: string;
  /** Override a format's description line, keyed by format id (obj/glb/stl). */
  formatDescriptions?: Partial<Record<ExportFormat["id"], string>>;
}

const DOWNLOAD_DEFAULTS = {
  heading: "Download 3D model",
  intro: "Pick a format. The download starts immediately when the file is ready.",
};

export interface DownloadModelModalProps {
  onClose: () => void;
  viewerRef: React.RefObject<ConfiguratorViewerHandle>;
  baseUrl?: string;
  /** Used to suggest a filename for the downloaded file. Series label is a
   *  reasonable default; falls back to "scene" when absent. */
  filenameStem?: string;
  /** Authorable presented copy; see DownloadModelModalContent. */
  content?: DownloadModelModalContent;
}

export function DownloadModelModal({
  onClose,
  viewerRef,
  baseUrl,
  filenameStem,
  content,
}: DownloadModelModalProps) {
  const heading = content?.heading?.trim() || DOWNLOAD_DEFAULTS.heading;
  const intro = content?.intro?.trim() || DOWNLOAD_DEFAULTS.intro;
  const formatDesc = (f: ExportFormat) =>
    content?.formatDescriptions?.[f.id]?.trim() || f.description;
  const [dmodelJson, setDmodelJson] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Fetch the DModel from Unity exactly once per mount. The viewer handle is
  // stable for the modal's lifetime (the cart override re-renders but
  // viewerRef.current is the same instance), so the effect's deps are empty.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const v = viewerRef.current;
    if (!v) {
      setFetchError("Viewer not active.");
      return;
    }
    v.callUnity("GetSceneDModel")
      .then(json => {
        logDModelSummary(json);
        setDmodelJson(json);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        setFetchError(`Couldn't get the scene from Designer: ${msg}`);
      });
  }, [viewerRef]);

  const exportFormat = async (format: ExportFormat) => {
    if (!dmodelJson) return;
    setExportingFormat(format.id);
    setExportError(null);
    try {
      const base = (baseUrl ?? "").replace(/\/+$/, "");
      const res = await fetch(`${base}/api/dmodel/export/${format.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: dmodelJson,
      });
      if (!res.ok) {
        // 501 NotImplemented is expected until the converter is wired —
        // surface a friendly message rather than a generic failure.
        if (res.status === 501) {
          setExportError(`${format.label} export isn't available yet.`);
        } else {
          setExportError(`Export failed (${res.status}).`);
        }
        return;
      }
      const blob = await res.blob();
      const stem = (filenameStem || "scene").replace(/[^\w\-]+/g, "-").toLowerCase() || "scene";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${stem}.${format.extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setExportError(`Network error: ${msg}`);
    } finally {
      setExportingFormat(null);
    }
  };

  const loading = dmodelJson == null && fetchError == null;

  return (
    <div className="sd-modal__inner sd-modal__inner--download-model">
      <header className="sd-modal__header">
        <h2>{heading}</h2>
        <button type="button" className="sd-modal__close" onClick={onClose} aria-label="Close">×</button>
      </header>
      <div className="sd-modal__body">
        {loading && (
          <p className="sd-download__status">Fetching scene from the designer…</p>
        )}
        {fetchError && (
          <p className="sd-download__status sd-download__status--error">{fetchError}</p>
        )}
        {dmodelJson && (
          <>
            <p className="sd-download__intro">{intro}</p>
            <ul className="sd-download__formats">
              {FORMATS.map(f => (
                <li key={f.id} className="sd-download__format">
                  <button
                    type="button"
                    className="sd-download__btn"
                    onClick={() => exportFormat(f)}
                    disabled={exportingFormat != null}
                  >
                    <span className="sd-download__btn-label">
                      {f.label}
                      {exportingFormat === f.id && <span className="sd-download__btn-spinner"> …</span>}
                    </span>
                    <span className="sd-download__btn-desc">{formatDesc(f)}</span>
                  </button>
                </li>
              ))}
            </ul>
            {exportError && (
              <p className="sd-download__status sd-download__status--error">{exportError}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
